"use client";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { industries } from "@/lib/data";
import { ArrowRight } from "lucide-react";
import React from "react";  

export const FlipCard = ({ image, details = [], hue = 220, social = [], name = "", description = "", slug = "" }:
  { image: string, details: string[], hue: number, social: { href: string, icon: React.ReactNode }[], name: string, description: string, slug: string }) => {
  return (
    <div
      className="relative w-[390px] h-[500px] perspective-[1000px] group cursor-pointer"
      style={{ "--hue": hue } as React.CSSProperties}
    >
      <div className="relative w-full h-full transition-all duration-700 [transform-style:preserve-3d] group-hover:rotate-y-180">
        {/* Card Front */}
        <div className="absolute w-full h-full bg-[hsl(var(--hue),25%,10%)] rounded-2xl overflow-hidden backface-hidden">
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover rounded-2xl"
          />
          <div className="absolute inset-0 bg-black/40 rounded-2xl"></div>
          <div className="absolute top-4 left-4 right-4 flex flex-col justify-between h-full">
            <h3 className="text-white text-2xl font-bold mb-2">{name}</h3>
            <p className="font-bold mb-6 text-white ">{description}</p>
          </div>
        </div>

        {/* Card Back */}
        <div className="absolute w-full h-full bg-[hsl(var(--hue),25%,10%)] rounded-2xl overflow-hidden backface-hidden rotate-y-180">
          <div className="absolute inset-0 bg-primary/40 rounded-2xl"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
            <h3 className="text-white text-2xl font-bold my-4">{name}</h3>
            <ul className="space-y-2 text-white/90 text-md mb-4">
              {details.map((item, index) => (
                <li key={index} className="flex items-center">
                  <span className="w-2 h-2 bg-white/60 rounded-full mr-2"></span>
                  {item}
                </li>
              ))}
            </ul>
            <Link href={`/industries/${slug}`}>
              <button className="px-6 py-3 bg-primary border border-primary  rounded-full font-semibold hover:bg-primary/80 transition-colors">
                Learn More
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function IndustriesPage() {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="w-full bg-secondary/50 py-20 md:py-28 relative overflow-hidden h-[20rem] flex items-center justify-center">
       
        <div className="container relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold font-headline text-primary mb-4 text-center">
            AI Solutions Tailored for Your Industry
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            We understand the unique challenges of your sector. Discover how our
            specialized AI solutions for local services and medical practices
            can help you thrive.
          </p>
        </div>
      </section>

      {/* Industries Section */}
      <section className="w-full py-16 md:py-24">
        <div className="container mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5  mx-auto">
            {industries.map((industry) => (
              <FlipCard
                key={industry.slug}
                image={industry.image}
                description={industry.description}
                name={industry.title}
                details={industry.subIndustries.map((sub) => sub.title)}
                hue={220}
                social={industry.subIndustries.map((sub) => ({ href: sub.title, icon: <sub.icon /> }))}
                slug={industry.slug}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
